import { useTranslation } from 'react-i18next';
import AmountFilter from '~/components/filters/AmountFilter';
import ClearFiltersButton from '~/components/filters/ClearFiltersButton';
import CollapsibleFilters, {
  AdditionalFilters,
  PrimaryFilters,
  MoreFiltersButtonWithDynamicLabels,
} from '~/components/filters/CollapsibleFilters';
import CounterpartFilter from '~/components/filters/CounterpartFilter';
import DateRangeFilter from '~/components/filters/DateRangeFilter';
import DocumentTypeFilter from '~/components/filters/DocumentTypeFilter';
import FlowPaymentStatusFilter from '~/components/filters/FlowPaymentStatusFilter';
import InlineSearchFilter from '~/components/filters/InlineSearchFilter';
import PaymentMethodFilter from '~/components/filters/PaymentMethodFilter';
import Toggle from '~/components/inputs/Toggle/Toggle';
import {
  useReconcileTransactionsFilters,
  useShowReconciledFlows,
  useUpdateReconcileTransactionsFilters,
} from '~/state/modals/reconcileTransactionModal';
import { useIsDirty } from '~/state/tableFilters';
import styles from './ReconcileSearchFilters.module.scss';

const ReconcileSearchFilters: React.FunctionComponent = () => {
  const filters = useReconcileTransactionsFilters();
  const updateFilter = useUpdateReconcileTransactionsFilters();
  const [showReconciled, setShowReconciled] = useShowReconciledFlows();
  const isDirty = useIsDirty();
  const { t } = useTranslation('reconciliations', { keyPrefix: 'reconcile_transaction_modal' });

  return (
    <CollapsibleFilters className={styles.filters}>
      <PrimaryFilters
        className={styles.primaryFiltersRow}
        showClearButton={false}
        showMoreFiltersButton={false}
        customFilterActions={<MoreFiltersButtonWithDynamicLabels />}
      >
        <InlineSearchFilter
          autoFocus
          className={styles.inlineSearchFilter}
          name="search"
          value={filters.search}
          label={t('search_label')}
          onChange={updateFilter}
          hideLabel
        />
      </PrimaryFilters>

      <AdditionalFilters className={styles.filtersRow}>
        <DateRangeFilter
          className={styles.dateRangeFilter}
          value={filters.flowPaymentDate}
          name="flowPaymentDate"
          label={t('payment_date_label')}
          onChange={(name, value) => updateFilter(name, value)}
          hideLabel
          clearable
        />
        <DateRangeFilter
          className={styles.dateRangeFilter}
          value={filters.documentDateRange}
          name="documentDateRange"
          label={t('issue_date_label')}
          onChange={(name, value) => updateFilter(name, value)}
          hideLabel
          clearable
        />
        <FlowPaymentStatusFilter
          className={styles.paymentStatusFilter}
          label={t('payment_status_label')}
          name="flowPaymentStatus"
          value={filters.flowPaymentStatus}
          onChange={(name, value) => updateFilter(name, value)}
          hideLabel
          clearable
        />
        <PaymentMethodFilter
          className={styles.paymentMethodFilter}
          value={filters.flowPaymentMethod}
          name="flowPaymentMethod"
          label={t('payment_type_label')}
          onChange={updateFilter}
          hideLabel
          clearable
        />
        <CounterpartFilter
          className={styles.counterpartFilter}
          name="counterpartId"
          label={t('counterpart_label')}
          placeholder={t('counterpart_label')}
          value={filters.counterpartId}
          onChange={(value) => updateFilter('counterpartId', value)}
          hideLabel
        />
        <DocumentTypeFilter
          className={styles.documentTypeFilter}
          name="documentType"
          label={t('document_type_label')}
          value={filters.documentType}
          onChange={updateFilter}
          hiddenTypes={[]}
          hideLabel
          clearable
        />
        <AmountFilter
          className={styles.amountFilter}
          showFlowDirection
          onChange={updateFilter}
          value={filters.totalAmount}
          name="totalAmount"
          popoverPlacement="bottom right"
          hideLabel
        />
        {isDirty && <ClearFiltersButton inline />}
      </AdditionalFilters>

      <Toggle
        className={styles.toggle}
        isSelected={showReconciled}
        label={t('show_reconciled_label')}
        onChange={setShowReconciled}
      />
    </CollapsibleFilters>
  );
};

export default ReconcileSearchFilters;
